import React from 'react';
import { 
  FaTruck, 
  FaClock, 
  FaMapMarkerAlt, 
  FaBox, 
  FaShieldAlt,
  FaEnvelope,
  FaInfoCircle,
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaGlobe
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './Shipping.css';

const Shipping = () => {
  return (
    <div className="shipping-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <Link to="/" className="back-link">
            <FaArrowLeft /> Back to Home
          </Link>
          <h1>Shipping Information</h1>
          <p>Everything you need to know about our shipping and delivery process</p>
        </div>

        {/* Main Content */}
        <div className="shipping-content">
          {/* Processing Time */}
          <div className="info-section">
            <div className="info-card">
              <FaClock className="icon" />
              <h3>Processing Time</h3>
              <p>Once your order is received, our team will contact you to confirm your size and measurements. This step ensures your garment is tailored to provide the perfect fit, reflecting the premium craftsmanship you expect.</p>
              <div className="highlight-box">
                <strong>Processing Period: 10-14 business days</strong>
              </div>
            </div>
          </div>

          {/* Delivery Time */}
          <div className="info-section">
            <div className="info-card">
              <FaTruck className="icon" />
              <h3>Delivery Time</h3>
              <p>Your order will be carefully crafted and shipped after the processing period. Once shipped, the estimated delivery time ranges between 15 to 20 business days depending on your location.</p>
              <div className="highlight-box">
                <strong>Estimated Delivery: 15-20 business days</strong>
              </div>
            </div>
          </div>

          {/* Shipping Charges */}
          <div className="info-section">
            <div className="info-card">
              <FaBox className="icon" />
              <h3>Shipping Charges</h3>
              <p>We offer free standard shipping on all orders above ₹2500. For orders below this amount or expedited shipping requests, additional charges may apply.</p>
              <div className="shipping-rates">
                <div className="rate-item">
                  <FaCheckCircle className="check-icon" />
                  <span>Orders above ₹2500: <strong>FREE</strong></span>
                </div>
                <div className="rate-item">
                  <FaExclamationTriangle className="warning-icon" />
                  <span>Orders below ₹2500: Additional charges apply</span>
                </div>
                <div className="rate-item">
                  <FaClock className="clock-icon" />
                  <span>Expedited shipping: Additional charges apply</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quality Assurance */}
          <div className="info-section">
            <div className="info-card">
              <FaShieldAlt className="icon" />
              <h3>Quality Assurance</h3>
              <p>We strive to ensure your customized or premium order reaches you promptly while maintaining the highest quality standards. Shipping times may vary slightly due to external courier factors or customs processing for international shipments.</p>
              <p>We appreciate your patience and promise the wait will be worth it for exceptional craftsmanship and a perfect fit.</p>
            </div>
          </div>

          {/* Shipping Process */}
          <div className="info-section">
            <h2>Our Shipping Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Order Confirmation</h4>
                  <p>We receive your order and send confirmation details</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Size & Measurement Confirmation</h4>
                  <p>Our team contacts you to confirm size and measurements for perfect fit</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Crafting Process</h4>
                  <p>Your garment is carefully crafted over 10-14 business days</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>Quality Check</h4>
                  <p>Final quality inspection before packaging</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h4>Shipping & Delivery</h4>
                  <p>Package is shipped with tracking details (15-20 business days)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Areas */}
          <div className="info-section">
            <div className="info-card">
              <FaMapMarkerAlt className="icon" />
              <h3>Delivery Areas</h3>
              <p>We deliver to all major cities and towns across India. Our delivery network covers:</p>
              <div className="delivery-areas">
                <div className="area-category">
                  <h4>Metro Cities</h4>
                  <p>Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad, Pune, Ahmedabad</p>
                </div>
                <div className="area-category">
                  <h4>Tier 2 Cities</h4>
                  <p>All major tier 2 cities with reliable courier services</p>
                </div>
                <div className="area-category">
                  <h4>Remote Areas</h4>
                  <p>Most remote areas with extended delivery timelines</p>
                </div>
              </div>
            </div>
          </div>

          {/* International Shipping */}
          <div className="info-section">
            <div className="info-card">
              <FaGlobe className="icon" />
              <h3>International Shipping</h3>
              <p>We currently ship within India only. International shipping options will be available soon. Please contact us for any special requests.</p>
              <div className="highlight-box">
                <strong>Currently: India Only</strong>
              </div>
            </div>
          </div>

          {/* Tracking Information */}
          <div className="info-section">
            <div className="info-card">
              <FaInfoCircle className="icon" />
              <h3>Order Tracking</h3>
              <p>Once your order is shipped, you will receive:</p>
              <ul className="tracking-features">
                <li>Tracking number via SMS and email</li>
                <li>Real-time delivery updates</li>
                <li>Estimated delivery date</li>
                <li>Courier service details</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="contact-section">
          <h3><FaInfoCircle /> Need Help with Shipping?</h3>
          <div className="contact-cards">
            <div className="contact-card">
              <FaEnvelope className="icon" />
              <h4>Email Us</h4>
              <p>info@vastrebyvijh.com</p>
              <small>Response within 24 hours</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shipping;
